import React, { useContext, useMemo } from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { useLoaderData } from 'react-router-dom';
import { useDeviceSize } from '@atlassian/learning-components';

import { HelmetContext } from 'src/components/Helmet';
import { useScreenEvent } from 'src/hooks/useScreenEvent';
import ContentSection, { SupportedContentSectionTypes } from 'src/pages/hubs/shared/children/ContentSection';
import PageHeading from 'src/pages/hubs/shared/children/PageHeading';

import { JiraHubLoaderProps } from './loader';

export const JiraHubPage: React.FC = () => {
  const helmet = useContext(HelmetContext);
  const deviceWidth = useDeviceSize();
  const isMobile = deviceWidth < 768;

  const { jiraHubData } = useLoaderData() as JiraHubLoaderProps;

  helmet.setHelmet({
    title: `${jiraHubData.titleExternal} | Learning - Atlassian Community`,
    seoTags: jiraHubData.seo,
  });

  // Send screen event for analytics
  useScreenEvent({ eventName: 'jiraHubScreen' });

  const contentSections = useMemo(
    () =>
      jiraHubData.contentContainerCollection.items.map((contentBlock) => (
        <ContentSection
          key={(contentBlock as SupportedContentSectionTypes).sys.id}
          contentBlock={contentBlock}
          optionalProps={{ containerMaxWidth: 1070 }}
        />
      )),
    [jiraHubData.contentContainerCollection.items],
  );

  return (
    <main data-testid="jira-hub" css={pageContainerStyles}>
      <PageHeading
        title={jiraHubData.hero.titleExternal}
        subtitle={jiraHubData.hero.subtitle}
        containerStyles={
          !isMobile
            ? {
                paddingTop: `calc(${token('space.1000')} + 15px)`,
                paddingBottom: `${token('space.600')}`,
              }
            : undefined
        }
        keyword="Jira"
        logoSrc={jiraHubData.hero.asset?.image.url || '/images/logos/jira.svg'}
        logoAlt="Jira logo"
        bannerDesktopSrc={jiraHubData.hero.backgroundImageDesktop?.image.url || '/images/hubs/jiraHubBannerDesktop.svg'}
        bannerMobileSrc={jiraHubData.hero.backgroundImageMobile?.image.url || '/images/hubs/jiraHubBannerMobile.svg'}
        bannerMobileAlt="Jira"
      />

      {/* content */}
      <div data-testid="jira-hub-content-section" css={outerContentContainerStyles}>
        <div css={innerContentContainerStyles}>{contentSections}</div>
      </div>
    </main>
  );
};

const pageContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '136px',
  gap: token('space.1000'),

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },

  '& [data-testid="hero"]': {
    margin: '0 auto',
  },

  [media.below.md]: {
    gap: token('space.600'),
  },
});

const outerContentContainerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  [media.below.md]: {
    paddingTop: 0,
  },
});

const innerContentContainerStyles = css({
  maxWidth: '1320px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: `calc(${token('space.1000')} * 2)`,
  alignSelf: 'center',

  [media.below.md]: {
    rowGap: `calc(${token('space.600')} * 2)`,

    '& > div': {
      paddingLeft: token('space.200'),
      paddingRight: token('space.200'),
    },
  },
});
